/**
 * Cart expiration manager.
 *
 * Extends the generic Timer with cart-specific behavior:
 * - Starts and refreshes cart expiration countdown
 * - Handles cart extension lifecycle
 * - Triggers warning and expiry events
 *
 * @example
 * ```ts
 * import { cartExpiration } from './CartExpiration';
 *
 * cartExpiration.startTimer({
 *   oldestSessionExpiryTime: '2026-07-21T18:30:00Z',
 * });
 * ```
 *
 */

import { Timer, TimerCallbacks, TimerState, TimerStatus } from '../../utils/timer';
import { eventUtil, getProcessEnvs } from '../../utils';
import { CartExpirationConfig } from './CartExpiration.types';

import { useMixedInventoryStore } from '../../store';
import { CUSTOM_EVENTS_CONSTANTS } from '../../constants/common';

export class CartExpiration {
  /**
   * Underlying countdown timer instance.
   */
  private readonly timer: Timer;

  /**
   * Time remaining threshold to show expiry warning,default 5 minutes(300000 ms) .
   */
  private warningThresholdMs = 300000;

  /**
   * Maximum allowed cart extensions, default 1 time.
   */
  private maxCartExtensions = 1;

  /**
   * A boolean state prevent duplicate warning events, triggered due to parent re-rendering and tick method behaviour
   */
  private warningTriggered = false;

  /**
   * Initializing zustand store
   */
  private store = useMixedInventoryStore.getState();

  /**
   * Creates cart expiration manager with timer callbacks, OnTick and OnCompleted
   */
  constructor() {
    const callbacks: TimerCallbacks = {
      onTick: this.timerTickHandler,
      onCompleted: this.showCartExpireModal,
    };
    this.timer = new Timer(callbacks);

    const { MI_CART_WARNING_THRESHOLD_MS, MI_MAX_CART_EXTENSIONS } = getProcessEnvs();
    this.warningThresholdMs = Number(MI_CART_WARNING_THRESHOLD_MS);
    this.maxCartExtensions = Number(MI_MAX_CART_EXTENSIONS);
  }

  /**
   * Starts cart expiration countdown.
   *
   * To be called after cart creation using the expiry time
   * returned from the Cart API.
   *
   * @example
   * ```ts
   * cartExpiration.startTimer({
   *   oldestSessionExpiryTime: cart.expirationTime,
   * });
   * ```
   *
   * Note: Expiry time should be provided in ISO date format.
   */
  public startTimer(config: CartExpirationConfig): void {
    this.warningTriggered = false;

    this.timer.start({
      expiryTime: config.oldestSessionExpiryTime,
    });
  }

  /**
   * Refreshes cart expiration timer using stored cart expiry time.
   *
   * Typically called after page reload or application initialization.
   *
   * @example
   * ```ts
   * cartExpiration.refreshTimerFromStore();
   * ```
   */
  public refreshTimerFromStore(): void {
    this.warningTriggered = false;

    if (this.store.cartState.expirationTime) {
      this.timer.start({
        expiryTime: this.store.cartState.expirationTime,
      });
    }
  }

  /**
   * Restarts timer after successful cart extension and updates the cartExtensionCount value in store
   *
   * @example
   * ```ts
   * cartExpiration.extendTimer({
   *   oldestSessionExpiryTime: updatedExpiryTime,
   * });
   * ```
   */
  public extendTimer(config: CartExpirationConfig): void {
    const updatedExtensionCount = this.store.cartState.cartExtensionCount + 1;
    this.store.setCartExtensionCount(updatedExtensionCount);
    this.warningTriggered = false;

    this.timer.start({
      expiryTime: config.oldestSessionExpiryTime,
    });
  }

  /**
   * Returns current timer display value.
   *
   * @returns Countdown formatted as `MM:SS`.
   *
   * @example
   * ```ts
   * cartExpiration.getTimerDisplayValue();
   * // "04:59"
   * ```
   */
  public getTimerDisplayValue(): string {
    return this.timer.getState().display.formatted;
  }

  /**
   * Handles timer updates and triggers warning event
   * when expiry threshold is reached.
   * Note: This is callback method passed while initializing timer in constructr, ad is called in Timer.tcike method
   */
  private timerTickHandler = (state: TimerState): void => {
    this.store = useMixedInventoryStore.getState(); // fetching the updated value of store
    const isExtensionCountValid = this.store.cartState.cartExtensionCount < this.maxCartExtensions; //checking if cart extension limit is reached
    const isThresholdReached = this.warningThresholdMs >= state.remainingTimeMs; //checking if thershold limit is reached to show the warning modal
    const isTimerRunning = state.status === TimerStatus.RUNNING; //cehcking if current state of timer is running

    /**
     * Condition to show warning modal
     *  - Note: !this.warningTriggered && isTimerRunning is to avoid the retriggering of warning modal due to re-render/tick behaviour
     */

    const showWarningModal = !this.warningTriggered && isTimerRunning && isExtensionCountValid && isThresholdReached;
    if (showWarningModal) {
      this.warningTriggered = true;
      this.dispatchCartModalEvent(CUSTOM_EVENTS_CONSTANTS.MI_SHOW_CART_EXPIRY_MODAL, { isExpired: false });
    }
  };

  /**
   * Handles timer completion, directly passed to timer class as callback
   * disptaches event to open expire modal
   */
  private showCartExpireModal = (): void => {
    this.dispatchCartModalEvent(CUSTOM_EVENTS_CONSTANTS.MI_SHOW_CART_EXPIRY_MODAL, { isExpired: true });
  };

  /**
   * Reusable method to dispatch cart expiration modal event.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dispatchCartModalEvent = (event: string, data: any): void => {
    eventUtil?.dispatch(event, data);
  };
}

/**
 * Shared CartExpiration instance.
 *
 * Keeps cart expiration state synchronized across components.
 *
 * @example
 * ```ts
 * cartExpiration.startTimer({
 *   oldestSessionExpiryTime: expiryTime,
 * });
 * ```
 */
export const cartExpiration = new CartExpiration();
